import { EncounterService } from '@quromedical/fhir-common';
import {
  type Admission,
  type AdmissionCreateRequest,
  admissionRequestSchemaFields,
} from '@quromedical/models';
import { CrudForm } from 'components/form';
import type { Field, SelectOption } from 'components/types';
import { admissionServiceDisplay } from 'core/display';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';
import type { ObjectType } from 'validation';
import * as yup from 'yup';

import { serviceClassMapping } from './internal';

export type AdmissionNotesData = Pick<
  AdmissionCreateRequest,
  'class' | 'service' | 'periodStart'
>;

// the class is left out of the form because we infer it based on the selected service because we
// have a simple setup right now, though this may become more complex as we add service types
type AdmissionNotesFormData = Pick<
  AdmissionCreateRequest,
  'service' | 'periodStart'
>;
interface AdmissionNotesFormProps {
  showErrors?: boolean;
  /**
   * Returns undefined if the data is incomplete or invalid. This is to simplify error checking and
   * validation at a parent level - no data means no valid data
   */
  onValidChange: (data?: AdmissionNotesData) => void;
  initialValues?: Partial<Admission>;
  patientId: string;
}

const serviceOptions: SelectOption[] = Object.values(EncounterService).map(
  (service) => ({
    value: service,
    display: admissionServiceDisplay[service],
  }),
);

const schema = yup
  .object<ObjectType<AdmissionNotesFormData>>({
    periodStart: admissionRequestSchemaFields.periodStart,
    service: admissionRequestSchemaFields.service,
  })
  .required();

const createFields = (): Field<AdmissionNotesFormData>[] => {
  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  lastMonthDate.setDate(1);

  return [
    {
      subfields: [
        {
          key: 'periodStart',
          type: 'date-component',
          label: strings.AdmissionStartLabel,
          maskType: 'date',
          validRange: { startDate: lastMonthDate },
        },
        {
          key: 'service',
          searchable: true,
          type: 'combobox-single',
          label: strings.AdmissionServiceLabel,
          fetcher: serviceOptions,
        },
      ],
    },
  ];
};

export const AdmissionNotesForm: React.FC<AdmissionNotesFormProps> = ({
  onValidChange,
  showErrors = false,
  initialValues,
}) => {
  const onChange = useCallback(
    (data?: Partial<AdmissionNotesFormData>) => {
      if (!data) {
        onValidChange(undefined);
      } else {
        const result = data as AdmissionNotesFormData; // casting since data is validated
        const fullData: AdmissionNotesData = {
          ...result,
          class: serviceClassMapping[result.service],
        };

        onValidChange(fullData);
      }
    },
    [onValidChange],
  );

  const fields = createFields();
  const initialData = {
    periodStart: initialValues?.period?.start,
    service: initialValues?.service,
  };
  return (
    <CrudForm<AdmissionNotesFormData>
      title={strings.AdmissionNotesFormTitle}
      cardProps={{ unsetZIndex: true }}
      includeIcons
      validateOnMount
      initialValues={initialData}
      fields={fields}
      validationSchema={schema}
      showErrors={showErrors}
      onValidChange={onChange}
      onChangeDebounceTime={0}
      showSubmitButton={false}
    />
  );
};
